import type { AnchorHTMLAttributes, ButtonHTMLAttributes, PropsWithChildren } from 'react';
import { createElement } from 'react';

import classNames from 'classnames';
import type { LocationDescriptor } from 'history';
import noop from 'lodash/noop';
import type { Link } from 'react-router-dom';

import styles from './styles.module.scss';

const validStyles = [
  'primary',
  'secondary',
  'newSecondary',
  'tertiary',
  'checkout',
  'orderTracking',
  'transfer',
  'transferInverted',
  'newDependent',
  'capsuleBlue50',
  'family',
];

export type ButtonStyles =
  | 'primary'
  | 'secondary'
  //This is the secondary from the new design system and should be used from now on
  | 'newSecondary'
  | 'tertiary'
  | 'checkout'
  | 'orderTracking'
  | 'transfer'
  | 'transferInverted'
  | 'newDependent'
  | 'capsuleBlue50'
  | 'family'
  | 'inlineLink';

export type ButtonSize = 'noPadding' | 'extraSmall' | 'small' | 'smallWide' | 'medium' | 'large';

export interface ButtonProps
  extends PropsWithChildren<{
    block?: boolean;
    className?: string;
    disabled?: boolean;
    fixedWidth?: boolean;
    id?: string;
    onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
    size?: ButtonSize;
    style?: ButtonStyles;
    tag?: 'button';
    to?: never;
    type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  }> {}

export interface LinkButtonProps extends Omit<ButtonProps, 'onClick' | 'tag' | 'to'> {
  onClick?: AnchorHTMLAttributes<HTMLAnchorElement>['onClick'];
  tag: typeof Link;
  to: LocationDescriptor<unknown>;
}

const Button = ({
  size = 'large',
  style = 'primary',
  block,
  disabled = false,
  className,
  onClick = noop,
  children,
  fixedWidth,
  tag = 'button',
  ...other
}: ButtonProps | LinkButtonProps) => {
  // When extending the button component to be a styled component, setting the style prop generates strange behavior.
  // For example, setting the style as "secondary" on the styled component reads as {0: "s", 1: "e", 2: "c"...9: "y"} on this underlying Button component.
  // The following code handles that problem.
  if (typeof style === 'object') {
    const styleString = Object.values(style).join('') as ButtonStyles;
    if (validStyles.includes(styleString)) {
      style = styleString;
    }
  }

  const buttonClassNames = classNames(styles.component, styles[size], styles[style], className, {
    [styles.disabled]: disabled,
    [styles.block]: block,
    [styles.fixedWidth]: fixedWidth,
  });

  const attributes = {
    onClick: disabled ? undefined : onClick,
    className: buttonClassNames,
    'aria-disabled': disabled,
    ...other,
  };

  // @ts-ignore
  return createElement(tag, attributes, children);
};

export default Button;
